interface BigFiveQuestion {
  id: string;
  trait: string;
  question: string;
  options: string[];
}

interface Big5QuestionsProps {
  answers: Record<string, number>;
  onAnswer: (questionId: string, value: number) => void;
  onContinue: () => void;
}

export const bigFiveQuestions: BigFiveQuestion[] = [
  {
    id: 'b1',
    trait: 'Openness',
    question: 'How do you approach new experiences?',
    options: [
      'I prefer familiar routines and established ways of doing things',
      'I occasionally try new things but generally stick with what I know',
      'I enjoy exploring new ideas and experiences regularly',
      'I constantly seek out novelty and love pushing boundaries',
    ],
  },
  {
    id: 'b2',
    trait: 'Conscientiousness',
    question: 'How do you manage tasks and responsibilities?',
    options: [
      "I tend to be spontaneous and don't worry much about organization",
      "I keep track of important things but don't stress about details",
      'I stay organized and complete most tasks on time',
      "I'm highly disciplined and always plan everything carefully",
    ],
  },
  {
    id: 'b3',
    trait: 'Extraversion',
    question: 'How do you recharge and socialize?',
    options: [
      'I prefer solitude and find social situations draining',
      'I enjoy small groups but need regular alone time',
      'I feel energized by social activities and meeting people',
      'I thrive in crowds and constantly seek social interaction',
    ],
  },
  {
    id: 'b4',
    trait: 'Agreeableness',
    question: 'How do you interact with others?',
    options: [
      'I prioritize my own needs and speak my mind directly',
      "I balance my needs with others' but stand firm when needed",
      "I'm considerate of others and try to avoid conflict",
      'I always put others first and go out of my way to help',
    ],
  },
  {
    id: 'b5',
    trait: 'Neuroticism',
    question: 'How do you handle stress and emotions?',
    options: [
      'I stay calm under pressure and rarely feel anxious',
      'I handle stress well but occasionally worry about things',
      'I often feel stressed and worry about various things',
      'I frequently experience anxiety and emotional ups and downs',
    ],
  },
];

export function Big5Questions({ answers, onAnswer, onContinue }: Big5QuestionsProps) {
  const allAnswered = bigFiveQuestions.every(q => answers[q.id] !== undefined);

  return (
    <div className="tw-space-y-3">
      <div className="tw-space-y-1">
        <h2 className="tw-text-xl tw-font-bold tw-text-gray-800">Tell us about yourself</h2>
      </div>

      <div className="tw-space-y-3">
        {bigFiveQuestions.map((q, qIndex) => (
          <div key={q.id} className="tw-space-y-1.5">
            <h3 className="tw-text-sm tw-font-semibold tw-text-gray-800">
              {qIndex + 1}. {q.question}
            </h3>
            <div className="tw-grid tw-grid-cols-4 tw-gap-2">
              {q.options.map((option, optionIndex) => {
                const value = optionIndex + 1;
                const isSelected = answers[q.id] === value;

                return (
                  <button
                    key={optionIndex}
                    onClick={() => onAnswer(q.id, value)}
                    className={`tw-p-2 tw-text-center tw-rounded-lg tw-transition-all tw-border-2 tw-min-h-[3rem] tw-flex tw-items-center tw-justify-center ${
                      isSelected
                        ? 'tw-border-blue-600 tw-bg-blue-50'
                        : 'tw-border-gray-200 tw-bg-white hover:tw-border-gray-300'
                    }`}
                  >
                    <span
                      className={`tw-text-xs tw-leading-tight ${
                        isSelected ? 'tw-text-blue-600 tw-font-medium' : 'tw-text-gray-800'
                      }`}
                    >
                      {option}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
        ))}
      </div>

      <div className="tw-flex tw-justify-end tw-pt-2">
        <button
          type="button"
          onClick={onContinue}
          disabled={!allAnswered}
          className="tw-rounded-full tw-px-6 tw-py-2 tw-text-sm tw-bg-blue-600 tw-text-white hover:tw-bg-blue-700 disabled:tw-opacity-50 disabled:tw-cursor-not-allowed"
        >
          Continue to Values
        </button>
      </div>
    </div>
  );
}
